// Signed actor context: how the Hub web Worker tells a Hub service who is
// acting. A service never trusts a person id or token the browser sent; it
// trusts only this header, and re-checks the capability it needs itself
// (ADR 0002).

export const ACTOR_HEADER = 'x-hub-actor'
const MAX_AGE_SECONDS = 60

export type ActorContext = {
  sub: string
  personId: string
  capabilities: string[]
  requestId: string
  // Seconds since the epoch, set by signActor.
  iat: number
}

const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

function fromBase64url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '==='.slice((value.length + 3) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(binary.length))
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

async function hmacKey(secret: string): Promise<CryptoKey> {
  if (!secret || secret.length < 32) throw new Error('Actor secret must be at least 32 characters')
  return crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify'])
}

export async function signActor(actor: Omit<ActorContext, 'iat'>, secret: string, now = Date.now()): Promise<string> {
  const payload = base64url(encoder.encode(JSON.stringify({ ...actor, iat: Math.floor(now / 1000) })))
  const signature = await crypto.subtle.sign('HMAC', await hmacKey(secret), encoder.encode(payload))
  return `${payload}.${base64url(new Uint8Array(signature))}`
}

// Returns null for anything that isn't a fresh header this secret signed.
export async function verifyActor(header: string | null | undefined, secret: string, now = Date.now()): Promise<ActorContext | null> {
  if (!header) return null
  const [payload, signature, extra] = header.split('.')
  if (!payload || !signature || extra !== undefined) return null

  let valid = false
  try {
    valid = await crypto.subtle.verify('HMAC', await hmacKey(secret), fromBase64url(signature), encoder.encode(payload))
  } catch {
    return null
  }
  if (!valid) return null

  let actor: ActorContext
  try {
    actor = JSON.parse(new TextDecoder().decode(fromBase64url(payload))) as ActorContext
  } catch {
    return null
  }
  const age = Math.floor(now / 1000) - actor.iat
  if (!Number.isInteger(actor.iat) || age < -5 || age > MAX_AGE_SECONDS) return null
  if (typeof actor.sub !== 'string' || typeof actor.personId !== 'string' || !Array.isArray(actor.capabilities)) return null
  return actor
}
